import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  position?: 'left' | 'right' | 'bottom';
  children: React.ReactNode;
  title?: string;
  showCloseButton?: boolean;
  className?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  position = 'left',
  children,
  title,
  showCloseButton = true,
  className,
}) => {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const getPositionClasses = () => {
    switch (position) {
      case 'left':
        return 'left-0 top-0 h-full w-[85vw] max-w-sm';
      case 'right':
        return 'right-0 top-0 h-full w-[85vw] max-w-sm';
      case 'bottom':
        return 'bottom-0 left-0 right-0 max-h-[90vh] rounded-t-2xl';
      default:
        return 'left-0 top-0 h-full w-[85vw] max-w-sm';
    }
  };

  const getAnimationClasses = () => {
    if (!isOpen) return '';
    switch (position) {
      case 'left':
        return 'drawer-enter-left';
      case 'right':
        return 'drawer-enter-right';
      case 'bottom':
        return 'drawer-enter-bottom';
      default:
        return 'drawer-enter-left';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={clsx(
          'fixed z-50 bg-white shadow-2xl transition-transform duration-250 ease-out flex flex-col',
          getPositionClasses(),
          getAnimationClasses(),
          'safe-top safe-bottom',
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Drawer'}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-4 py-4 border-b border-rm-neutral-600/20">
            {title && (
              <h2 className="text-lg font-semibold text-rm-ink-900">{title}</h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className={clsx(
                  'touch-target rounded-full p-2',
                  'hover:bg-rm-bg-50 transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-accent-500',
                  !title && 'ml-auto'
                )}
                aria-label="Close drawer"
              >
                <X className="w-5 h-5 text-rm-ink-900" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto flex-1 pb-4">
          {children}
        </div>
      </div>
    </>
  );
};
